<template>
  <div>
    <!-- Título -->
    <b-row class="align-items-center mb-1">
      <b-col><h1 class="font-weight-bolder">
        Cuentas por Pagar (Payables)
      </h1></b-col>
      <b-col cols="auto">
        <b-input-group
          size="sm"
          class="mr-1"
          style="width: 240px;"
        >
          <b-form-input
            v-model.trim="searchDraft"
            placeholder="Buscar folio"
            :disabled="loading"
            @keyup.enter="applySearch"
          />
          <b-input-group-append>
            <b-button
              variant="primary"
              :disabled="loading"
              @click="applySearch"
            >
              Buscar
            </b-button>
          </b-input-group-append>
        </b-input-group>

        <b-button
          variant="outline-secondary"
          :disabled="loading"
          @click="printList"
        >
          Imprimir
        </b-button>

      </b-col>
    </b-row>

    <!-- Filtros -->
    <b-card class="mb-1">
      <b-row>
        <b-col md="4">
          <b-form-group label="Proveedor">
            <b-form-select
              v-model="filters.supplier_id"
              :options="supplierOptions"
              :value-field="'value'"
              :text-field="'text'"
              :disabled="loading"
              @change="onSupplierChange"
            />
          </b-form-group>
        </b-col>

        <b-col
          md="4"
          class="d-flex align-items-center"
        >
          <b-form-checkbox
            v-model="filters.unpaid_only"
            switch
            class="mr-2"
            @change="refetch"
          >
            Sólo no pagadas
          </b-form-checkbox>
          <b-form-checkbox
            v-model="filters.only_overdue"
            switch
            @change="refetch"
          >
            Sólo vencidas
          </b-form-checkbox>
        </b-col>
      </b-row>
    </b-card>

    <!-- Tabla -->
    <b-overlay :show="loading">
      <payables-table
        :rows="rows"
        @pay-full="onPayFull"
        @pay-partial="onPayPartial"
        @save-invoice="onSaveInvoice"
        @revert-payment="onRevertPayment"
      />
    </b-overlay>

    <b-alert
      v-if="!loading && rows.length === 0"
      show
      variant="light"
      class="mt-1"
    >
      No hay registros 🧾
    </b-alert>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PayablesTable from '@/views/finance/PayablesTable.vue'

export default {
  components: { PayablesTable },

  data() {
    return {
      loading: false,
      supplierNameCache: {},
      suppressRouteSync: false,

      // OJO: nunca reasignar este objeto completo
      filters: {
        supplier_id: null,
        unpaid_only: true,
        only_overdue: false,
        q: '',
      },
      searchDraft: '',
    }
  },

  computed: {
    ...mapGetters('finance', ['payables']),
    rows() { return this.payables },

    supplierOptions() {
      const opts = [{ value: null, text: 'Todos' }]

      Object.entries(this.supplierNameCache).forEach(([id, name]) => {
        opts.push({ value: id, text: name })
      })

      const selectedId = this.filters.supplier_id
      if (selectedId && !this.supplierNameCache[selectedId]) {
        opts.push({ value: selectedId, text: 'Proveedor seleccionado' })
      }

      return opts
    },
  },

  watch: {
    '$route.query': {
      immediate: false,
      handler(query) {
        if (this.suppressRouteSync) return
        const next = this.parseQuery(query || {})
        if (JSON.stringify(next) === JSON.stringify(this.currentFilters())) return
        this.applyFilters(next)
        this.refetch()
      },
    },
    filters: {
      deep: true,
      handler() {
        this.persistFilters()
        this.syncRouteWithFilters()
      },
    },
    rows: {
      immediate: true,
      handler(list) {
        (list || []).forEach(r => {
          const id = r?.supplier?.id // <-- CAMBIO
          const name = r?.supplier?.name
          if (id && name && !this.supplierNameCache[id]) {
            this.$set(this.supplierNameCache, id, name)
          }
        })
      },
    },
  },

  created() {
    this.hydrateFilters()
    this.refetch()
  },

  methods: {
    ...mapActions('finance', [
      'fetchPayables',
      'setPurchaseOrderInvoice',
      'payPurchaseOrderFull',
      'payPurchaseOrderPartial',
      'revertPurchaseOrderPayment',
    ]),
    money(v) {
      const n = Number(v || 0)
      return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
    },
    currentFilters() {
      return {
        supplier_id: this.filters.supplier_id || null,
        unpaid_only: this.filters.unpaid_only,
        only_overdue: this.filters.only_overdue,
        q: this.filters.q || '',
      }
    },
    parseQuery(q) {
      const toBool = (v, defaultValue) => {
        if (v === undefined || v === null || v === '') return defaultValue
        return v === '1' || v === 'true' || v === true
      }

      return {
        supplier_id: q.supplier_id ? String(q.supplier_id) : null,
        unpaid_only: toBool(q.unpaid_only, true),
        only_overdue: toBool(q.only_overdue, false),
        q: q.q ? String(q.q) : '',
      }
    },
    serializeFilters() {
      const query = {}
      if (this.filters.supplier_id) query.supplier_id = String(this.filters.supplier_id)
      if (this.filters.q) query.q = this.filters.q
      query.unpaid_only = this.filters.unpaid_only ? '1' : '0'
      query.only_overdue = this.filters.only_overdue ? '1' : '0'
      return query
    },
    applyFilters(next) {
      this.filters.supplier_id = next.supplier_id || null
      this.filters.unpaid_only = next.unpaid_only
      this.filters.only_overdue = next.only_overdue
      this.filters.q = next.q || ''
      this.searchDraft = this.filters.q
    },
    persistFilters() {
      if (typeof window === 'undefined') return
      window.sessionStorage.setItem('payables_filters', JSON.stringify(this.currentFilters()))
    },
    hydrateFilters() {
      const fromRoute = this.parseQuery(this.$route.query || {})
      const hasRouteValues = Object.keys(this.$route.query || {}).length > 0
      if (hasRouteValues) {
        this.applyFilters(fromRoute)
        return
      }

      if (typeof window === 'undefined') return
      const raw = window.sessionStorage.getItem('payables_filters')
      if (!raw) return

      try {
        const parsed = JSON.parse(raw)
        if (parsed && typeof parsed === 'object') {
          this.applyFilters({
            supplier_id: parsed.supplier_id ? String(parsed.supplier_id) : null,
            unpaid_only: typeof parsed.unpaid_only === 'boolean' ? parsed.unpaid_only : true,
            only_overdue: typeof parsed.only_overdue === 'boolean' ? parsed.only_overdue : false,
            q: parsed.q ? String(parsed.q) : '',
          })
        }
      } catch (_) {
        // noop
      }
    },
    syncRouteWithFilters() {
      if (this.suppressRouteSync) return
      const nextQuery = this.serializeFilters()
      const currQuery = this.$route.query || {}
      if (JSON.stringify(nextQuery) === JSON.stringify(currQuery)) return

      this.suppressRouteSync = true
      this.$router.replace({ query: nextQuery }).catch(() => {})
      this.$nextTick(() => {
        this.suppressRouteSync = false
      })
    },

    // handler EXPLÍCITO para el select
    onSupplierChange(val) {
      this.filters.supplier_id = val || null
      this.refetch()
    },

    applySearch() {
      this.filters.q = String(this.searchDraft || '').trim()
      this.refetch()
    },
    printList() { window.print() },

    async refetch() {
      const snapshot = {
        supplier_id: this.filters.supplier_id, // <-- CAMBIO
        unpaid_only: this.filters.unpaid_only,
        only_overdue: this.filters.only_overdue,
        q: this.filters.q,
      }

      console.log('REFETCH snapshot ->', snapshot)

      this.loading = true
      try {
        await this.fetchPayables(snapshot)
      } finally {
        this.loading = false
      }
    },

    async onSaveInvoice({ poId, invoiceNumber, done }) {
      this.loading = true
      try {
        await this.setPurchaseOrderInvoice({ poId, invoiceNumber })
        await this.refetch()
        if (typeof done === 'function') done()
      } finally { this.loading = false }
    },

    async onPayFull(poId) {
      this.loading = true
      try {
        await this.payPurchaseOrderFull({ poId })
        await this.refetch()
      } finally { this.loading = false }
    },

    async onRevertPayment(poId) {
      this.loading = true
      try {
        await this.revertPurchaseOrderPayment({ poId })
        await this.refetch()
      } finally { this.loading = false }
    },

    async onPayPartial({ poId, amount }) {
      this.loading = true
      try {
        await this.payPurchaseOrderPartial({ poId, amount })
        await this.refetch()
      } finally { this.loading = false }
    },
  },
}
</script>
